import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { selectCurrentProvider } from '../../../store/provider';
import {
  requestImportById,
  requestCreateNewImport,
  selectCurrentImport,
  selectImportUploading,
  requestImportFileUpload,
} from '../../../store/imports';
import { bindActionCreators } from 'redux';
import { useThunkDispatch } from '../../../Helpers/Action.helpers';
import { useInterval } from '../../../Hooks/useInterval/useInterval';
import { selectErrors } from '~/store/app';
import { IMPORT_ACTIONS } from '~/Constants/Actions.constants';

export const useUploadBox = () => {
  const dispatch = useThunkDispatch();

  const currentProvider = useSelector(selectCurrentProvider);
  const currentImport = useSelector(selectCurrentImport);
  const uploading = useSelector(selectImportUploading);

  const error = useSelector(selectErrors(IMPORT_ACTIONS.POST_IMPORT_FILE));

  const getImportById = bindActionCreators(requestImportById, dispatch);
  const createNewImport = (id: string) => dispatch(requestCreateNewImport(id));
  const uploadFile = (id: string, file: FormData) =>
    currentProvider && dispatch(requestImportFileUpload(id, currentProvider.uuid, file));

  const onDrop = useCallback(async (acceptedFiles: any) => {
    if (!currentProvider) return;

    const importFile = new FormData();
    importFile.append('import', acceptedFiles[0]);

    const importObject = await createNewImport(currentProvider.uuid);
    await uploadFile(importObject.id, importFile);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isFocused } = useDropzone({
    onDrop,
    accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const shouldPoll =
    !error &&
    currentImport?.id &&
    currentProvider?.uuid &&
    (currentImport?.status === 'New' || currentImport?.status === 'ParsingFile');

  useInterval(
    () => {
      if (currentImport && currentProvider) {
        getImportById(currentImport.id, currentProvider.uuid);
      }
    },
    shouldPoll ? 1000 : null,
  );

  return {
    rootProps: getRootProps(),
    inputProps: getInputProps(),
    isActive: isDragActive,
    uploading,
    isFocused,
    uploadStatus: currentImport?.status,
    importError: error,
  };
};
